import {
    Button,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Link,
    Radio,
    RadioGroup,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Markdown from '/@/components/markdown';
import settings from '/@/core/settings';
import { type AccessRequestCreated, type AccessRequestType, createAccessRequestApi } from '/@/hooks/access-requests';
import { _ } from '/@/locale';
import { getAppInstance, getSessionCountry } from '/@/store/session/selectors';
import { validateEmail } from '/@/utils';
import AREA_CODES from '/@/utils/area-codes';
import { formatPhoneNumber, validatePhoneNumber } from '/@/utils/phone-numbers';
import { reverse } from '/@/utils/router';
import RequestAccessSubmitted, { AccessRequestScreen, RequestAccessUnderReview } from './submitted';

/**
 * Sent by the create endpoint when this email already has a request waiting for
 * review. Keyed off the code rather than the message, which is translated.
 */
const PENDING_REQUEST_ERROR_CODE = 'pending_access_request';

/** Built per render so the labels follow a language switch made on this screen. */
const roleOptions = (): { value: AccessRequestType; label: string }[] => [
    { value: 'rv', label: _('Representante de Ventas') },
    { value: 'advisor', label: _('Asesor Agronómico') },
    { value: 'consultant', label: _('Consultor') },
];

const sectionLabelSx = { fontWeight: 700 };
const inputSx = {};

interface FormValues {
    name: string;
    email: string;
    phone: string;
    type: AccessRequestType;
    distributor: string;
    referent: string;
}

const RequestAccess = () => {
    const appInstance = useSelector(getAppInstance);
    const country = useSelector(getSessionCountry);
    const navigate = useNavigate();
    const [screen, setScreen] = useState<'form' | 'submitted' | 'underReview'>('form');
    const [error, setError] = useState('');

    const {
        control,
        handleSubmit,
        watch,
        formState: { isValid, isSubmitting },
    } = useForm<FormValues>({
        mode: 'onChange',
        defaultValues: { name: '', email: '', phone: '', type: 'rv', distributor: '', referent: '' },
    });

    // Only the Advisor flow asks which distributor the person belongs to.
    const showDistributor = watch('type') === 'advisor';

    const onSubmit = async (values: FormValues) => {
        setError('');
        try {
            const created: AccessRequestCreated = await createAccessRequestApi({
                appInstance: appInstance.id,
                name: values.name.trim(),
                email: values.email.trim(),
                // Normalize here so the stored value is one `signUp` will accept later,
                // when the user creates a password and can no longer edit this field.
                phone: formatPhoneNumber(values.phone),
                type: values.type,
                distributor: showDistributor ? values.distributor.trim() : '',
                referent: values.referent.trim(),
            });

            if (created.status === 'accepted' && created.token) {
                // Already a validated user in Identity. An email that also already has
                // an account has nothing to create, so it goes to the login screen
                // instead of the password one.
                if (created.userExists) {
                    navigate(reverse('ExistingAccountLogin', { params: { token: created.token } }));
                    return;
                }
                navigate(reverse('CreatePassword', { params: { token: created.token } }), {
                    state: { autoAccepted: true },
                });
                return;
            }
            setScreen('submitted');
        } catch (requestError) {
            const detail = requestError?.response?.data;
            // DRF turns each value of the error dict into a list of strings.
            const code = Array.isArray(detail?.code) ? detail.code[0] : detail?.code;
            if (code === PENDING_REQUEST_ERROR_CODE) {
                setScreen('underReview');
                return;
            }
            setError(
                detail?.email?.[0] ||
                    detail?.appInstance?.[0] ||
                    detail?.detail ||
                    _('Ha ocurrido un error, por favor reintente.')
            );
        }
    };

    const renderForm = () => (
        <Stack spacing={2}>
            <Typography variant='h5' color='primary' sx={{ fontWeight: 700 }}>
                {_('Solicitá acceso a {{appName}}', { appName: settings.app.name })}
            </Typography>
            <Typography variant='subtitle1' color='primary' sx={{ mt: 1 }}>
                <Markdown
                    components={{
                        p: ({ children }) => (
                            <Typography variant='subtitle1' color='primary' sx={{ mt: 1 }}>
                                {children}
                            </Typography>
                        ),
                        strong: ({ children }) => (
                            <Typography variant='subtitle1' color='primary' component='span' fontWeight='bold'>
                                {children}
                            </Typography>
                        ),
                    }}
                >
                    {_(
                        'Este formulario es para el **equipo comercial y Distribuidores autorizados por Bayer**. Completá tus datos y nuestro equipo se pone en contacto para confirmar tu acceso.'
                    )}
                </Markdown>
            </Typography>

            <Typography variant='subtitle1' color='primary' sx={sectionLabelSx}>
                {_('Datos personales')}
            </Typography>
            <Stack spacing={1}>
                <Controller
                    name='name'
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                        <TextField
                            fullWidth
                            placeholder={_('Nombre y apellido')}
                            value={field.value}
                            onChange={field.onChange}
                            sx={inputSx}
                        />
                    )}
                />
                <Controller
                    name='email'
                    control={control}
                    // `validateEmail` returns `true` or the error message -- exactly the
                    // shape react-hook-form's `validate` expects.
                    rules={{ required: true, validate: validateEmail }}
                    render={({ field, fieldState }) => (
                        <TextField
                            fullWidth
                            type='email'
                            placeholder={_('E-Mail')}
                            value={field.value}
                            onChange={field.onChange}
                            error={Boolean(fieldState.error)}
                            helperText={fieldState.error?.message}
                            sx={inputSx}
                        />
                    )}
                />
                <Controller
                    name='phone'
                    control={control}
                    rules={{
                        required: true,
                        validate: (value) =>
                            validatePhoneNumber(value) || _('El formato del número de teléfono es incorrecto.'),
                    }}
                    render={({ field, fieldState }) => (
                        // Same `react-phone-input-2` setup as the profile edit tab, so the
                        // country prefix, area codes and MX mask behave identically.
                        <FormControl fullWidth sx={{ ...inputSx, height: 'auto' }} ref={field.ref}>
                            <PhoneInput
                                value={field.value}
                                onBlur={field.onBlur}
                                onChange={(_value, _data, _event, formattedValue) => field.onChange(formattedValue)}
                                specialLabel=''
                                country={country?.code?.toLowerCase()}
                                enableAreaCodeStretch
                                enableAreaCodes
                                areaCodes={AREA_CODES}
                                masks={{ mx: '... .... ....' }}
                                buttonStyle={{ border: 'none' }}
                                inputStyle={{ border: 'none', height: '100%', borderRadius: 0, width: '100%' }}
                                containerStyle={{ border: '1px solid #0000003b', borderRadius: 0 }}
                            />
                            <FormHelperText error={Boolean(fieldState.error)}>
                                {fieldState.error?.message}
                            </FormHelperText>
                        </FormControl>
                    )}
                />
            </Stack>

            <Typography variant='subtitle1' color='primary' sx={sectionLabelSx}>
                {_('¿Cuál es tu rol?')}
            </Typography>
            <Controller
                name='type'
                control={control}
                render={({ field }) => (
                    <RadioGroup
                        value={field.value}
                        onChange={(event) => field.onChange(event.target.value)}
                        sx={{
                            px: 1.5,
                        }}
                    >
                        {roleOptions().map((option) => (
                            <FormControlLabel
                                key={option.value}
                                value={option.value}
                                control={
                                    <Radio
                                        color='secondary'
                                        sx={{
                                            mr: 0.5,
                                        }}
                                    />
                                }
                                label={option.label}
                            />
                        ))}
                    </RadioGroup>
                )}
            />

            {showDistributor && (
                <>
                    <Typography variant='subtitle1' color='primary' sx={sectionLabelSx}>
                        {_('Distribuidor')}
                    </Typography>
                    <Controller
                        name='distributor'
                        control={control}
                        render={({ field }) => (
                            <TextField
                                fullWidth
                                placeholder={_('Ej: Distribuidores Centro')}
                                value={field.value}
                                onChange={field.onChange}
                                sx={inputSx}
                            />
                        )}
                    />
                </>
            )}

            <Stack>
                <Typography variant='subtitle1' color='primary' sx={sectionLabelSx}>
                    {_('Referente en Bayer (opcional)')}
                </Typography>
                <Typography variant='caption' color='primary.mui.inactive'>
                    {_(
                        'Si conocés al RV, Consultor o Coach que te puede confirmar, contanos quién es. Acelera la revisión.'
                    )}
                </Typography>
            </Stack>
            <Controller
                name='referent'
                control={control}
                render={({ field }) => (
                    <TextField
                        fullWidth
                        placeholder={_('Escribir el nombre del referente')}
                        value={field.value}
                        onChange={field.onChange}
                        sx={inputSx}
                    />
                )}
            />

            {error && (
                <Typography variant='caption' sx={{ color: 'error.main' }}>
                    {error}
                </Typography>
            )}

            <Stack direction='row' justifyContent={{ xs: 'stretch', md: 'flex-end' }}>
                <Button
                    variant='contained'
                    disabled={!isValid || isSubmitting || !appInstance}
                    onClick={handleSubmit(onSubmit)}
                    sx={{ width: { xs: '100%', md: 'auto' } }}
                >
                    {isSubmitting ? _('Enviando...') : _('Enviar solicitud')}
                </Button>
            </Stack>

            <Typography variant='subtitle2' color='primary'>
                {_('Si tenés alguna duda escribinos a ')}
                <Link href={`mailto:${settings.app.infoEmail}`} color='secondary'>
                    {settings.app.infoEmail}
                </Link>
            </Typography>
        </Stack>
    );

    const backToLogin = () => navigate(reverse('Login'));

    return (
        <AccessRequestScreen onBack={backToLogin}>
            {screen === 'submitted' && <RequestAccessSubmitted onBackToStart={backToLogin} />}
            {screen === 'underReview' && <RequestAccessUnderReview onBackToStart={backToLogin} />}
            {screen === 'form' && renderForm()}
        </AccessRequestScreen>
    );
};

export default RequestAccess;
